// frontend/src/pages/admin/DeclareWinnerPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Draw, UserInfo } from '../../types';

const DeclareWinnerPage: React.FC = () => {
    const { id: drawId } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [draw, setDraw] = useState<Draw | null>(null);
    const [winningNumber, setWinningNumber] = useState<number | ''>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string>('');
    const [success, setSuccess] = useState<string>('');

    const userInfoString = localStorage.getItem('userInfo');
    const user: UserInfo | null = userInfoString ? JSON.parse(userInfoString) : null;

    useEffect(() => {
        const fetchDraw = async () => {
            if (!user || !user.token || !user.isAdmin) {
                setError('Unauthorized: You must be logged in as an administrator.');
                setLoading(false);
                return;
            }
            if (!drawId) {
                setError('Draw ID is missing.');
                setLoading(false);
                return;
            }

            try {
                // Ensure REACT_APP_API_BASE_URL is a simple string, no complex escapes needed
                const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
                if (!API_BASE_URL) {
                    throw new Error("REACT_APP_API_BASE_URL is not defined in your environment variables.");
                }

                const res = await fetch(`${API_BASE_URL}/draws/${drawId}`, {
                    headers: {
                        'Authorization': `Bearer ${user.token}`,
                    },
                });

                const data = await res.json();

                if (!res.ok) {
                    throw new Error(data.message || 'Failed to fetch draw details.');
                }

                if (data.status !== 'closed') {
                    setError('Winner can only be declared for a closed draw.');
                    setLoading(false);
                    return;
                }

                setDraw(data);
            } catch (err: any) {
                setError((err instanceof Error ? err.message : String(err)) || 'Error fetching draw details.');
            } finally {
                setLoading(false);
            }
        };

        fetchDraw();
    }, [drawId, user]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // Modified validation: Allow any number including 0
        if (winningNumber === '' || winningNumber < 0) { // Removed the upper limit check
            setError('Please enter a valid winning number (0 or greater).'); // Updated error message
            return;
        }

        if (!user || !user.token || !user.isAdmin) {
            setError('Unauthorized: You must be logged in as an administrator.');
            return;
        }

        if (!drawId) {
            setError('Draw ID is missing.');
            return;
        }

        setLoading(true);
        try {
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
            const res = await fetch(`${API_BASE_URL}/draws/${drawId}/declare-winner`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${user.token}`,
                },
                body: JSON.stringify({ winningNumber }),
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.message || 'Failed to declare winner.');
            }

            setSuccess(`Winner declared! Winning number: ${data.draw.winningNumber}. Total Payout: Rs. ${data.totalPayoutAmount}`);
            setLoading(false);
            setTimeout(() => {
                navigate('/admin/draws/manage');
            }, 3000);
        } catch (err: any) {
            setError((err instanceof Error ? err.message : String(err)) || 'Error declaring winner.');
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="container mx-auto p-4 mt-8 text-center">
                <p className="text-lg text-gray-700">Loading draw details...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mx-auto p-4 mt-8 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
                <p>{error}</p>
                <button onClick={() => navigate('/admin/draws/manage')} className="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded">
                    Go back to Manage Draws
                </button>
            </div>
        );
    }

    if (!draw) {
        return (
            <div className="container mx-auto p-4 mt-8 bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative">
                <p>Draw not found or inaccessible.</p>
                <button onClick={() => navigate('/admin/draws/manage')} className="mt-4 bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded">
                    Go back to Manage Draws
                </button>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4 mt-8 max-w-md bg-white rounded-lg shadow-md border border-gray-200">
            <h1 className="text-2xl font-bold text-center mb-6 text-gray-800">Declare Winner for Draw</h1>

            <div className="mb-4">
                <p className="text-lg font-semibold text-blue-700">{draw.name}</p>
                <p className="text-sm text-gray-600">Deadline: {new Date(draw.deadline).toLocaleString()}</p>
                <p className="text-md text-gray-700">Entry Cost: Rs. {draw.entryCost}</p>
                <p className="text-md text-gray-700">Prize Pool: {draw.prizeValue}{draw.prizePoolType === 'percentage' ? '%' : ' Rs.'}</p>
                <p className="text-md text-gray-700 font-semibold">Status: <span className="text-yellow-600">{draw.status.toUpperCase()}</span></p>
            </div>

            {success && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4">{success}</div>}
            {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4">{error}</div>}

            <form onSubmit={handleSubmit}>
                <div className="mb-4">
                    <label htmlFor="winningNumber" className="block text-gray-700 text-sm font-bold mb-2">
                        Winning Number: {/* Changed label text */}
                    </label>
                    <input
                        type="number"
                        id="winningNumber"
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        min="0" // Keep min="0" to allow 0 and positive numbers
                        // Removed max="9"
                        value={winningNumber}
                        onChange={(e) => setWinningNumber(parseInt(e.target.value) || '')}
                        required
                        disabled={loading}
                    />
                </div>
                <div className="flex items-center justify-between">
                    <button
                        type="submit"
                        className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:opacity-50"
                        disabled={loading}
                    >
                        {loading ? 'Declaring...' : 'Declare Winner'}
                    </button>
                    <button
                        type="button"
                        onClick={() => navigate('/admin/draws/manage')}
                        className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline disabled:opacity-50"
                        disabled={loading}
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </div>
    );
};

export default DeclareWinnerPage;